import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Save, 
  X, 
  Plus,
  Trash2,
  Send,
  CheckCircle,
  User,
  Mail,
  Phone,
  AlertCircle,
  FileText,
  Calendar,
  Settings,
  Bell,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  Download,
  UploadCloud,
} from 'lucide-react';
import ContractService from '../../services/contractService';
import ESignatureService from '../../services/eSignatureService';
import SourcingESignatureService from '../../services/sourcingESignatureService';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';
import ConfirmationModal from '../common/ConfirmationModal';

const ESignatureEdit = ({ module: moduleProp = 'contract' }) => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  const isRfpModule = moduleProp === 'rfp';
  const basePath = isRfpModule ? '/sourcing/e-signature' : '/contracts/e-signature';
  const apiService = isRfpModule ? SourcingESignatureService : ESignatureService;
  // Both modules now use contractId as the primary relationship field.
  // isRfpModule requests are created directly from a contract (no RFP lookup).
  const sourceFieldName = 'contractId';
  const moduleLabel = 'Contract';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingOriginal, setUploadingOriginal] = useState(false);
  const [uploadingSigned, setUploadingSigned] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [document, setDocument] = useState(null);
  const [searchParams] = useSearchParams();
  // Tracks document URLs independently so they update without a full reload.
  const [documentUrls, setDocumentUrls] = useState({
    originalDocumentUrl: null,
    originalContractUrl: null,
    signatureFileUrl: null,
    signedDocumentUrl: null,
  });

  const STEPS = [
    { id: 0, label: 'Document Details', icon: FileText },
    { id: 1, label: 'Recipients', icon: User },
    { id: 2, label: 'Signature Settings', icon: Settings },
    { id: 3, label: 'Signature Progress', icon: CheckCircle }
  ];

  // Form state
  const [formData, setFormData] = useState({
    documentName: '',
    documentType: 'Contract',
    contractId: '',
    priority: 'Medium',
    dueDate: '',
    signatureMethod: 'Electronic Signature',
    authenticationMethod: 'Email Verification',
    reminderEnabled: true,
    reminderFrequency: 'daily',
    maxReminders: 3,
    notes: '',
    recipients: []
  });

  // Load document data
  useEffect(() => {
    loadDocumentData();
  }, [documentId, searchParams]);

  const loadDocumentData = async (overrideId) => {
    const activeId = overrideId || documentId;
    setLoading(true);
    try {
      if (activeId === 'new') {
        // ── Contract-centric: read contractId + contractMongoId from URL ──
        const contractIdParam = searchParams.get('contractId');
        const contractMongoIdParam = searchParams.get('contractMongoId');
        if (isRfpModule && contractIdParam) {
          const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0];

          const signatureDoc = {
            id: contractIdParam,
            documentName: contractIdParam, // will be overridden by title if available
            documentType: 'Contract',
            contractId: contractIdParam,
            contractMongoId: contractMongoIdParam || contractIdParam,
            priority: 'Medium',
            dueDate,
            signatureMethod: 'Electronic Signature',
            authenticationMethod: 'Email Verification',
            reminderEnabled: true,
            reminderFrequency: 'daily',
            maxReminders: 3,
            notes: '',
            recipients: [],
            status: 'Pending Signature',
            completionPercentage: 0,
          };

          // Try to load contract details for a better document name
          try {
            const contractRes = await ContractService.getContractById(
              contractMongoIdParam || contractIdParam
            );
            const c = contractRes?.data ?? contractRes;
            if (c) {
              signatureDoc.documentName = c.title || contractIdParam;
              signatureDoc.notes = c.description || '';
            }
          } catch (cErr) {
            console.warn('Could not pre-load contract details:', cErr);
          }

          // Build originalContractUrl for the signature backend
          const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
          const contractMongoId = contractMongoIdParam || contractIdParam;
          const originalContractUrl = `${apiBase}/contracts/${contractMongoId}/generate-docx`;

          setDocument(signatureDoc);
          setFormData({
            documentName: signatureDoc.documentName,
            documentType: signatureDoc.documentType,
            contractId: signatureDoc.contractId,
            priority: signatureDoc.priority,
            dueDate: signatureDoc.dueDate,
            signatureMethod: signatureDoc.signatureMethod,
            authenticationMethod: signatureDoc.authenticationMethod,
            reminderEnabled: signatureDoc.reminderEnabled,
            reminderFrequency: signatureDoc.reminderFrequency,
            maxReminders: signatureDoc.maxReminders,
            notes: signatureDoc.notes,
            recipients: signatureDoc.recipients,
          });
          setDocumentUrls((prev) => ({
            ...prev,
            originalContractUrl,
            originalDocumentUrl: originalContractUrl,
          }));
          setLoading(false);
          return;
        }
        setLoading(false);
        return;
      }

      // Try loading from e-signature API
      try {
        const response = await apiService.getESignatureRequestById(activeId);
        const req = response?.data ?? response;
        const rawRecipients = req.recipients || req.signers || [];

        const signatureDoc = {
          id: req.requestId || req.id,
          documentName: req.documentName,
          documentType: req.documentType,
          [sourceFieldName]: req[sourceFieldName] || '',
          priority: req.priority || 'Medium',
          dueDate: req.dueDate ? new Date(req.dueDate).toISOString().split('T')[0] : '',
          signatureMethod: req.signatureMethod || 'Electronic Signature',
          authenticationMethod: req.authenticationMethod || 'Email Verification',
          reminderEnabled: req.reminderEnabled !== undefined ? req.reminderEnabled : true,
          reminderFrequency: req.reminderFrequency || 'daily',
          maxReminders: req.maxReminders || 3,
          notes: req.notes || '',
          recipients: rawRecipients.map((r, i) => ({
            id: r._id || `recipient-${Date.now()}-${i}`,
            name: r.name || '',
            email: r.email || '',
            phone: r.phone || '',
            role: r.role || 'Signer',
            order: r.order || i + 1,
            status: r.status || 'Pending',
            signedAt: r.signedAt || null,
            authenticationMethod: r.authenticationMethod || 'Email Verification'
          })),
          status: req.status || 'Pending Signature',
          createdDate: req.createdDate || new Date().toISOString(),
          lastActivity: req.lastActivity || new Date().toISOString(),
          completionPercentage: req.completionPercentage || 0
        };

        setDocument(signatureDoc);
        setFormData({
          documentName: signatureDoc.documentName,
          documentType: signatureDoc.documentType,
          [sourceFieldName]: signatureDoc[sourceFieldName],
          priority: signatureDoc.priority,
          dueDate: signatureDoc.dueDate,
          signatureMethod: signatureDoc.signatureMethod,
          authenticationMethod: signatureDoc.authenticationMethod,
          reminderEnabled: signatureDoc.reminderEnabled,
          reminderFrequency: signatureDoc.reminderFrequency,
          maxReminders: signatureDoc.maxReminders,
          notes: signatureDoc.notes,
          recipients: signatureDoc.recipients
        });
        setDocumentUrls({
          originalDocumentUrl: req.originalDocumentUrl || req.documentUrl || null,
          originalContractUrl: req.originalContractUrl || req.originalDocumentUrl || req.documentUrl || null,
          signatureFileUrl: req.signatureFileUrl || null,
          signedDocumentUrl: req.signedDocumentUrl || null,
        });
        setLoading(false);
        return;
      } catch (eSigError) {
        // Not found in e-signature API, try source API for contract module
        if (isRfpModule) {
          console.warn('RFP e-signature request not found, starting fresh');
          setLoading(false);
          return;
        }
      }

      // Fallback: load from contract service (contract module only)
      if (!isRfpModule) {
        const response = await ContractService.getContractById(documentId);
        const contract = response.data;
        
        const signatureDoc = {
          id: contract.contractId || contract.id,
          documentName: contract.title || contract.name || 'Untitled Contract',
          documentType: contract.type || 'Contract',
          contractId: contract.contractId || contract.id,
          priority: contract.priority || 'Medium',
          dueDate: contract.endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          signatureMethod: 'Electronic Signature',
          authenticationMethod: 'Email Verification',
          reminderEnabled: true,
          reminderFrequency: 'daily',
          maxReminders: 3,
          notes: contract.description || '',
          recipients: [
            {
              id: `recipient-${Date.now()}`,
              name: contract.counterparty || 'Unknown',
              email: contract.counterpartyContact?.email || '',
              phone: contract.counterpartyContact?.phone || '',
              role: 'Signer',
              order: 1,
              status: contract.status === 'EXECUTED' ? 'Completed' : 'Pending',
              signedAt: contract.status === 'EXECUTED' ? contract.updatedAt : null,
              authenticationMethod: 'Email Verification'
            }
          ],
          status: contract.status === 'EXECUTED' ? 'Completed' : 
                  contract.status === 'IN_REVIEW' ? 'In Progress' : 'Pending Signature',
          createdDate: contract.createdAt || new Date().toISOString(),
          lastActivity: contract.updatedAt || contract.createdAt || new Date().toISOString(),
          completionPercentage: contract.status === 'EXECUTED' ? 100 : 
                                contract.status === 'IN_REVIEW' ? 50 : 0
        };

        setDocument(signatureDoc);
        setFormData({
          documentName: signatureDoc.documentName,
          documentType: signatureDoc.documentType,
          contractId: signatureDoc.contractId,
          priority: signatureDoc.priority,
          dueDate: signatureDoc.dueDate,
          signatureMethod: signatureDoc.signatureMethod,
          authenticationMethod: signatureDoc.authenticationMethod,
          reminderEnabled: signatureDoc.reminderEnabled,
          reminderFrequency: signatureDoc.reminderFrequency,
          maxReminders: signatureDoc.maxReminders,
          notes: signatureDoc.notes,
          recipients: signatureDoc.recipients
        });
      }
    } catch (error) {
      console.error('Error loading document:', error);
      showError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Recipient management
  const handleAddRecipient = () => {
    const newRecipient = {
      id: `recipient-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      email: '',
      phone: '',
      role: 'Signer',
      order: formData.recipients.length + 1,
      status: 'Pending',
      signedAt: null,
      authenticationMethod: 'Email Verification'
    };
    setFormData(prev => ({
      ...prev,
      recipients: [...prev.recipients, newRecipient]
    }));
  };

  const handleRemoveRecipient = (recipientId) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients
        .filter(r => r.id !== recipientId)
        .map((r, index) => ({ ...r, order: index + 1 }))
    }));
  };

  const handleRecipientChange = (recipientId, field, value) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.map(r => 
        r.id === recipientId ? { ...r, [field]: value } : r
      )
    }));
  };

  const handleMoveRecipient = (recipientId, direction) => {
    setFormData(prev => {
      const recipients = [...prev.recipients];
      const index = recipients.findIndex(r => r.id === recipientId);
      if (index === -1) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= recipients.length) return prev;
      
      [recipients[index], recipients[newIndex]] = [recipients[newIndex], recipients[index]];
      recipients.forEach((r, i) => { r.order = i + 1; });
      
      return { ...prev, recipients };
    });
  };

  const buildRequestPayload = () => ({
    documentName: formData.documentName,
    documentType: formData.documentType,
    [sourceFieldName]: formData[sourceFieldName],
    priority: formData.priority,
    dueDate: formData.dueDate,
    signatureMethod: formData.signatureMethod,
    authenticationMethod: formData.authenticationMethod,
    reminderEnabled: formData.reminderEnabled,
    reminderFrequency: formData.reminderFrequency,
    maxReminders: formData.maxReminders,
    notes: formData.notes,
    signers: formData.recipients.map(({ id, ...r }) => ({
      ...r,
      signedAt: r.signedAt || undefined,
    })),
  });

  // Save document
  const handleSave = async () => {
    setSaving(true);
    try {
      if (!formData.documentName.trim()) {
        showError('Document name is required');
        setSaving(false);
        return;
      }
      if (formData.recipients.length === 0) {
        showError('At least one recipient is required');
        setSaving(false);
        return;
      }
      if (formData.recipients.some(r => !r.name || !r.email)) {
        showError('All recipients must have a name and email');
        setSaving(false);
        return;
      }

      const requestData = buildRequestPayload();

      if (documentId && documentId !== 'new') {
        await apiService.updateESignatureRequest(documentId, requestData);
        success('Document updated successfully');
        await loadDocumentData(documentId);
      } else {
        const response = await apiService.createESignatureRequest(requestData);
        const created = response?.data ?? response;
        const newId = created?.requestId || created?.id;
        success('Document created successfully');
        if (newId) {
          navigate(`${basePath}/edit/${newId}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error saving document:', error);
      showError('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  // Send for signature
  const handleSendForSignature = async () => {
    setSending(true);
    try {
      if (formData.recipients.length === 0) {
        showError('At least one recipient is required');
        return;
      }
      if (formData.recipients.some(r => !r.name || !r.email)) {
        showError('All recipients must have a name and email');
        return;
      }
      if (!documentId || documentId === 'new') {
        showError('Please save the document before sending for signature');
        return;
      }

      await apiService.updateESignatureRequest(documentId, buildRequestPayload());
      await apiService.sendESignatureRequest(documentId);

      success('Document sent for signature successfully');
      setShowSendModal(false);
      await loadDocumentData(documentId);
    } catch (error) {
      console.error('Error sending document:', error);
      showError(error?.response?.data?.message || 'Failed to send document for signature');
    } finally {
      setSending(false);
    }
  };

  // Send reminder
  const handleSendReminder = async (recipientId) => {
    try {
      const recipient = formData.recipients.find(r => r.id === recipientId);
      if (!recipient) return;

      // Simulate sending reminder
      await new Promise(resolve => setTimeout(resolve, 500));
      success(`Reminder sent to ${recipient.name || recipient.email}`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError('Failed to send reminder');
    }
  };

  // Delete document
  const handleDelete = async () => {
    try {
      if (documentId && documentId !== 'new') {
        await apiService.deleteESignatureRequest(documentId);
      }
      success('Document deleted successfully');
      navigate(basePath);
    } catch (error) {
      console.error('Error deleting document:', error);
      showError('Failed to delete document');
    }
  };

  // Navigation functions
  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (idx) => {
    if (idx <= currentStep) setCurrentStep(idx);
  };

  const handleCancel = () => {
    navigate(basePath);
  };

  // ── Document upload handlers ────────────────────────────────────────
  const handleUploadOriginal = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!documentId || documentId === 'new') {
      showError(isRfpModule ? 'Please save the request before uploading a signature image.' : 'Please save the request before attaching a document.');
      return;
    }
    setUploadingOriginal(true);
    try {
      const result = isRfpModule
        ? await apiService.uploadSignatureImage(documentId, file)
        : await apiService.uploadOriginalDocument(documentId, file);
      setDocumentUrls(prev => ({
        ...prev,
        originalDocumentUrl: result?.data?.originalDocumentUrl || prev.originalDocumentUrl,
        signatureFileUrl: result?.data?.signatureFileUrl || prev.signatureFileUrl,
      }));
      success(isRfpModule ? 'Signature image uploaded successfully' : 'Original document uploaded successfully');
    } catch (err) {
      showError(err?.response?.data?.message || (isRfpModule ? 'Failed to upload signature image' : 'Failed to upload original document'));
    } finally {
      setUploadingOriginal(false);
      // Reset the file input so the same file can be re-selected
      e.target.value = '';
    }
  };

  const handleUploadSigned = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!documentId || documentId === 'new') {
      showError('Please save the request before attaching the signed document.');
      return;
    }
    setUploadingSigned(true);
    try {
      const result = await apiService.uploadSignedDocument(documentId, file);
      setDocumentUrls(prev => ({
        ...prev,
        signedDocumentUrl: result?.data?.signedDocumentUrl || prev.signedDocumentUrl,
      }));
      success('Signed document uploaded successfully');
    } catch (err) {
      showError(err?.response?.data?.message || 'Failed to upload signed document');
    } finally {
      setUploadingSigned(false);
      e.target.value = '';
    }
  };

  const headerActions = [
    {
      label: 'Cancel',
      variant: 'outline',
      onClick: handleCancel,
      disabled: saving || sending
    },
    {
      label: saving ? 'Saving...' : 'Save',
      icon: <Save size={18} />,
      variant: 'primary',
      onClick: handleSave,
      disabled: saving || sending
    }
  ];

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="E-Signature Document"
          description="Loading document details..."
          actions={[]}
        />
        <div style={{ padding: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            flexDirection: 'column',
            gap: '16px',
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            padding: '40px',
          }}>
            <div className="loading-spinner"></div>
            <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-family)' }}>
              Loading document...
            </p>
          </div>
        </div>
      </>
    );
  }

  // Step renderers
  const renderStep0 = () => (
    <div>
      <h3 style={{ 
        marginBottom: '24px', 
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-family)',
        fontSize: '18px',
        fontWeight: '600'
      }}>
        Document Information
      </h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Document Name *
          </label>
          <input
            type="text"
            value={formData.documentName}
            onChange={(e) => handleChange('documentName', e.target.value)}
            className="form-control"
            placeholder="Enter document name"
          />
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Document Type
          </label>
            <select
              value={formData.documentType}
              onChange={(e) => handleChange('documentType', e.target.value)}
              className="form-control"
            >
              {isRfpModule ? (
                <>
                  <option value="Contract">Contract</option>
                  <option value="Vendor Contract">Vendor Contract</option>
                  <option value="SOW">SOW</option>
                  <option value="NDA">NDA</option>
                  <option value="Framework Agreement">Framework Agreement</option>
                </>
              ) : (
                <>
                  <option value="Contract">Contract</option>
                  <option value="Employment Contract">Employment Contract</option>
                  <option value="Vendor Contract">Vendor Contract</option>
                  <option value="NDA">NDA</option>
                  <option value="License Agreement">License Agreement</option>
                  <option value="Lease Agreement">Lease Agreement</option>
                </>
              )}
            </select>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {isRfpModule ? 'RFP ID' : 'Contract ID'}
          </label>
          <input
            type="text"
            value={formData[sourceFieldName]}
            readOnly
            className="form-control"
            style={{ backgroundColor: '#f3f4f6', color: 'var(--text-secondary)' }}
          />
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => handleChange('priority', e.target.value)}
            className="form-control"
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Due Date *
          </label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => handleChange('dueDate', e.target.value)}
            className="form-control"
          />
        </div>
      </div>

      <div>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-family)',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={4}
          className="form-control"
          placeholder="Add any additional notes or instructions..."
          style={{ resize: 'vertical' }}
        />
      </div>

      {/* ── Documents Section ─────────────────────────────────────────────── */}
      <div style={{ marginTop: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '2px solid var(--border-color)',
        }}>
          <FileText size={18} style={{ color: '#4678be' }} />
          <h4 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
          }}>
            Documents
          </h4>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {isRfpModule ? (
            <>
              {/* Signature to Append — the ONLY document card shown during creation */}
              <div style={{
                border: '1px solid var(--border-color)',
                borderRadius: '0',
                padding: '20px',
                backgroundColor: '#fafafa',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      marginBottom: '6px',
                    }}>Signature to Append</div>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-family)' }}>
                      Upload the signature image that will be appended to the contract document.
                    </p>
                    {documentUrls.signatureFileUrl && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                        <CheckCircle size={16} style={{ color: '#059669', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', color: '#059669', fontFamily: 'var(--font-family)', fontWeight: '500' }}>Signature image uploaded</span>
                      </div>
                    )}
                  </div>
                  <label id="esig-upload-signature-label" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '13px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: 'var(--text-primary)',
                    cursor: uploadingOriginal ? 'not-allowed' : 'pointer',
                    backgroundColor: 'white',
                    opacity: uploadingOriginal ? 0.6 : 1,
                  }}>
                    <UploadCloud size={14} />
                    {uploadingOriginal ? 'Uploading...' : 'Upload Signature'}
                    <input type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg" style={{ display: 'none' }} disabled={uploadingOriginal} onChange={handleUploadOriginal} />
                  </label>
                </div>
              </div>
            </>
          ) : (
            <>

          {/* ─ Original Document card ─ always shown regardless of status */}
          <div style={{
            border: '1px solid var(--border-color)',
            borderRadius: '0',
            padding: '20px',
            backgroundColor: '#fafafa',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '6px',
                }}>
                  Original Document
                </div>
                {documentUrls.originalDocumentUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <FileText size={16} style={{ color: '#4678be', flexShrink: 0 }} />
                    <span style={{
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-family)',
                      fontWeight: '500',
                      wordBreak: 'break-all',
                    }}>
                      {formData.documentName || 'Original Document'}
                    </span>
                  </div>
                ) : (
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)',
                    fontStyle: 'italic',
                  }}>
                    No document attached. Upload a PDF or DOCX below.
                  </p>
                )}
              </div>

              {/* Download + Replace buttons */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {documentUrls.originalDocumentUrl && (
                  <a
                    id="esig-download-original-btn"
                    href={documentUrls.originalDocumentUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor: '#4678be',
                      color: 'white',
                      textDecoration: 'none',
                      fontSize: '13px',
                      fontWeight: '500',
                      fontFamily: 'var(--font-family)',
                      borderRadius: '0',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <Download size={14} />
                    Download
                  </a>
                )}

                {/* Upload / Replace trigger */}
                <label
                  id="esig-upload-original-label"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '13px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: 'var(--text-primary)',
                    cursor: uploadingOriginal ? 'not-allowed' : 'pointer',
                    backgroundColor: 'white',
                    opacity: uploadingOriginal ? 0.6 : 1,
                  }}
                >
                  <UploadCloud size={14} />
                  {uploadingOriginal ? 'Uploading…' : documentUrls.originalDocumentUrl ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    style={{ display: 'none' }}
                    disabled={uploadingOriginal}
                    onChange={handleUploadOriginal}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* ─ Signed Document card ─ shown always (upload only enabled when Completed) */}
          <div style={{
            border: `1px solid ${documentUrls.signedDocumentUrl ? '#059669' : 'var(--border-color)'}`,
            borderRadius: '0',
            padding: '20px',
            backgroundColor: documentUrls.signedDocumentUrl ? '#f0fdf4' : '#fafafa',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: documentUrls.signedDocumentUrl ? '#059669' : 'var(--text-secondary)',
                  fontFamily: 'var(--font-family)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '6px',
                }}>
                  Signed Document
                </div>
                {documentUrls.signedDocumentUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <CheckCircle size={16} style={{ color: '#059669', flexShrink: 0 }} />
                    <span style={{
                      fontSize: '14px',
                      color: '#059669',
                      fontFamily: 'var(--font-family)',
                      fontWeight: '500',
                    }}>
                      Signed copy available
                    </span>
                  </div>
                ) : (
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)',
                    fontStyle: 'italic',
                  }}>
                    {document?.status === 'Completed'
                      ? 'All signatures complete. Upload the signed copy below.'
                      : 'Available once all signers have completed.'}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {documentUrls.signedDocumentUrl && (
                  <a
                    id="esig-download-signed-btn"
                    href={documentUrls.signedDocumentUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      backgroundColor: '#059669',
                      color: 'white',
                      textDecoration: 'none',
                      fontSize: '13px',
                      fontWeight: '500',
                      fontFamily: 'var(--font-family)',
                      borderRadius: '0',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <Download size={14} />
                    Download Signed
                  </a>
                )}

                {/* Upload signed — only enabled when Completed */}
                {document?.status === 'Completed' && (
                  <label
                    id="esig-upload-signed-label"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      border: '1px solid #059669',
                      borderRadius: '0',
                      fontSize: '13px',
                      fontWeight: '500',
                      fontFamily: 'var(--font-family)',
                      color: '#059669',
                      cursor: uploadingSigned ? 'not-allowed' : 'pointer',
                      backgroundColor: 'white',
                      opacity: uploadingSigned ? 0.6 : 1,
                    }}
                  >
                    <UploadCloud size={14} />
                    {uploadingSigned ? 'Uploading…' : documentUrls.signedDocumentUrl ? 'Replace Signed' : 'Upload Signed'}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      style={{ display: 'none' }}
                      disabled={uploadingSigned}
                      onChange={handleUploadSigned}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

            </>
          )}
        </div>
      </div>
      {/* ── End Documents Section ──────────────────────────────────────────── */}
    </div>
  );

  const renderStep1 = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ 
          margin: 0, 
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-family)',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          Recipients ({formData.recipients.length})
        </h3>
        <button
          type="button"
          onClick={handleAddRecipient}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#4678be',
            color: 'white',
            border: 'none',
            borderRadius: '0',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)'
          }}
        >
          <Plus size={16} />
          Add Recipient
        </button>
      </div>

      {formData.recipients.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-family)',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          backgroundColor: '#f9fafb'
        }}>
          <User size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>No recipients added. Click "Add Recipient" to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formData.recipients.map((recipient, index) => (
            <div
              key={recipient.id}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: '0',
                padding: '20px',
                backgroundColor: 'white'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <GripVertical size={20} style={{ color: 'var(--text-secondary)', cursor: 'move' }} />
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#4678be',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)'
                  }}>
                    {recipient.order}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleMoveRecipient(recipient.id, 'up')}
                      style={{
                        padding: '6px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                      title="Move up"
                    >
                      ↑
                    </button>
                  )}
                  {index < formData.recipients.length - 1 && (
                    <button
                      type="button"
                      onClick={() => handleMoveRecipient(recipient.id, 'down')}
                      style={{
                        padding: '6px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                      title="Move down"
                    >
                      ↓
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveRecipient(recipient.id)}
                    style={{
                      padding: '6px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      color: '#EF4444'
                    }}
                    title="Remove recipient"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={recipient.name}
                    onChange={(e) => handleRecipientChange(recipient.id, 'name', e.target.value)}
                    className="form-control"
                    placeholder="Recipient name"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={recipient.email}
                    onChange={(e) => handleRecipientChange(recipient.id, 'email', e.target.value)}
                    className="form-control"
                    placeholder="recipient@example.com"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={recipient.phone}
                    onChange={(e) => handleRecipientChange(recipient.id, 'phone', e.target.value)}
                    className="form-control"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '12px',
                    fontWeight: '500',
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-family)'
                  }}>
                    Role
                  </label>
                  <select
                    value={recipient.role}
                    onChange={(e) => handleRecipientChange(recipient.id, 'role', e.target.value)}
                    className="form-control"
                  >
                    <option value="Signer">Signer</option>
                    <option value="Approver">Approver</option>
                    <option value="Witness">Witness</option>
                    <option value="CC">CC (Copy)</option>
                  </select>
                </div>
              </div>

              {recipient.status === 'Completed' && recipient.signedAt && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#D1FAE5',
                  border: '1px solid #059669',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  <CheckCircle size={16} style={{ color: '#059669' }} />
                  <span style={{ fontSize: '14px', color: '#059669', fontFamily: 'var(--font-family)' }}>
                    Signed on {new Date(recipient.signedAt).toLocaleDateString()} at {new Date(recipient.signedAt).toLocaleTimeString()}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h3 style={{ 
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-family)',
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '0'
      }}>
        Signature Configuration
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Signature Method
          </label>
          <select
            value={formData.signatureMethod}
            onChange={(e) => handleChange('signatureMethod', e.target.value)}
            className="form-control"
          >
            <option value="Electronic Signature">Electronic Signature</option>
            <option value="Digital Signature">Digital Signature</option>
            <option value="Advanced Electronic Signature">Advanced Electronic Signature</option>
          </select>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Authentication Method
          </label>
          <select
            value={formData.authenticationMethod}
            onChange={(e) => handleChange('authenticationMethod', e.target.value)}
            className="form-control"
          >
            <option value="Email Verification">Email Verification</option>
            <option value="Email + SMS">Email + SMS</option>
            <option value="Email + Phone">Email + Phone</option>
            <option value="Email + SMS + ID Verification">Email + SMS + ID Verification</option>
          </select>
        </div>
      </div>

      <div style={{
        border: '1px solid var(--border-color)',
        borderRadius: '0',
        padding: '20px',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <input
            type="checkbox"
            checked={formData.reminderEnabled}
            onChange={(e) => handleChange('reminderEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px' }}
          />
          <label style={{ 
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            margin: '0'
          }}>
            Enable Automatic Reminders
          </label>
        </div>

        {formData.reminderEnabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginLeft: '30px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '12px',
                fontWeight: '500',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-family)'
              }}>
                Reminder Frequency
              </label>
              <select
                value={formData.reminderFrequency}
                onChange={(e) => handleChange('reminderFrequency', e.target.value)}
                className="form-control"
              >
                <option value="daily">Daily</option>
                <option value="every-2-days">Every 2 Days</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '12px',
                fontWeight: '500',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-family)'
              }}>
                Maximum Reminders
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.maxReminders}
                onChange={(e) => handleChange('maxReminders', parseInt(e.target.value))}
                className="form-control"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3 style={{ 
        marginBottom: '24px', 
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-family)',
        fontSize: '18px',
        fontWeight: '600'
      }}>
        Signature Progress
      </h3>

      {/* Overall Progress */}
      <div style={{
        border: '1px solid var(--border-color)',
        borderRadius: '0',
        padding: '24px',
        marginBottom: '24px',
        backgroundColor: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)'
          }}>
            Overall Progress
          </span>
          <span style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#4678be',
            fontFamily: 'var(--font-family)'
          }}>
            {document?.completionPercentage || 0}%
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '12px',
          backgroundColor: '#e5e7eb',
          borderRadius: '0',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${document?.completionPercentage || 0}%`,
            height: '100%',
            backgroundColor: '#4678be',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          <span>{formData.recipients.filter(r => r.status === 'Completed').length} of {formData.recipients.length} signed</span>
          <span>Status: {document?.status || 'Pending Signature'}</span>
        </div>
      </div>

      {/* Recipients Progress */}
      <div>
        <h4 style={{
          marginBottom: '16px',
          fontSize: '16px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-family)'
        }}>
          Recipient Status
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {formData.recipients.map((recipient) => (
            <div
              key={recipient.id}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: '0',
                padding: '16px',
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: recipient.status === 'Completed' ? '#059669' : '#F59E0B',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)'
                  }}>
                    {recipient.order}
                  </span>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {recipient.name || 'Unnamed Recipient'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {recipient.email} • {recipient.role}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span className={`badge ${
                  recipient.status === 'Completed' ? 'badge-success' :
                  recipient.status === 'In Progress' ? 'badge-primary' :
                  'badge-warning'
                }`}>
                  {recipient.status}
                </span>
                {recipient.status !== 'Completed' && (
                  <button
                    type="button"
                    onClick={() => handleSendReminder(recipient.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <Bell size={14} />
                    Send Reminder
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title={formData.documentName || 'E-Signature Document'}
        description={`Manage e-signature document: ${formData[sourceFieldName] || 'New Document'}`}
        actions={headerActions}
      />
      <div style={{ padding: '24px' }}>
        {/* Draft Notice Banner */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 24px',
          backgroundColor: 'var(--accent-color)',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          fontSize: '14px',
          color: 'var(--text-primary)',
          marginBottom: '24px',
          fontFamily: 'var(--font-family)'
        }}>
          <AlertCircle size={18} style={{ marginRight: '12px', color: 'var(--gold-primary)' }} />
          <div>
            <strong>E-Signature Setup:</strong> Configure document details, recipients, and signature settings before sending for signatures.
          </div>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden'
        }}>
          {/* Multi-step Header */}
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <div style={{ padding: '32px' }}>
            <form onSubmit={currentStep === STEPS.length - 1 ? handleSave : handleNext}>
              {/* Animated Current Step */}
              <div key={currentStep} style={{ animation: 'fadeIn 0.25s ease-in' }}>
                {stepRenderers[currentStep]()}
              </div>

              {/* Navigation Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '32px 0 0 0',
                borderTop: '1px solid var(--border-color)',
                marginTop: '40px'
              }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-secondary"
                    style={{ minWidth: '100px', borderRadius: '0', padding: '12px 24px' }}
                  >
                    <X size={16} />
                    Cancel
                  </button>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="btn btn-secondary"
                      style={{
                        minWidth: '100px',
                        borderRadius: '0',
                        padding: '12px 24px',
                        border: '1px solid var(--gold-primary)',
                        color: 'var(--gold-primary)'
                      }}
                    >
                      <ChevronLeft size={16} />
                      Back
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {/* Step dots */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {STEPS.map((_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: idx === currentStep ? '20px' : '8px',
                          height: '8px',
                          borderRadius: '4px',
                          background: idx < currentStep ? 'var(--gold-bright)' : idx === currentStep ? 'var(--gold-primary)' : 'var(--divider-color)',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </div>

                  {currentStep < STEPS.length - 1 ? (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{
                        minWidth: '130px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: 'var(--gold-primary)',
                        border: 'none'
                      }}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowSendModal(true)}
                      disabled={sending}
                      className="btn btn-success"
                      style={{
                        minWidth: '160px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: document?.status === 'Completed' ? 'var(--text-muted)' : '#059669',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: document?.status === 'Completed' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Send size={16} />
                      {document?.status === 'Completed' ? 'Already Signed' : 'Send for Signature'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Send for Signature Modal */}
      <ConfirmationModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onConfirm={handleSendForSignature}
        title="Send for Signature"
        message={`Are you sure you want to send "${formData.documentName}" for signature to ${formData.recipients.length} recipient(s)?`}
        confirmText={sending ? 'Sending...' : 'Send'}
        cancelText="Cancel"
        type="primary"
        isLoading={sending}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${formData.documentName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Global Fade In Animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default ESignatureEdit;

